import React, {useMemo} from "react";
import { useGetRelativeDate, DateFormats } from "../../../hooks/report/useGetRelativeDate";
import { PersonAndBoxMessage } from "../../../components/messages/PersonAndBoxMessage";
import { useSnackbar } from "../../../context/snackbar-context";
import { useSnackbarDisplayOnError } from "../../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../../hooks/request/useGetBoundReportPaginated";
import { useGetBoundReportSummary } from "../../../hooks/request/useGetBoundReportSummary";
import { BoundTablePagination } from "../../../components/common/inputs/boundInputs";
import { InvoiceTransactionVO } from "../../../types/model/valueObject/invoice/InvoiceTransactionVO";
import { BuyerInvoicesSummary, buyerInvoicesSummaryMapping } from "../../../types/model/valueObject/invoice/BuyerInvoicesSummary";
import { BuyerInvoicesSortFilter, BuyerInvoicesSearchParams } from "../../../components/buyer/invoicesReport/BuyerInvoicesSortFilter";
import { BUYER_INVOICES_DATE_BYPASS_KEYS } from "../../../types/model/criteria/invoice/BuyerInvoiceSearchCriteria";
import { withDateRangeBypass } from "../../../util/ReportDateRangeUtils";
import { BuyerInvoicesCardListWithLoader } from "../../../components/buyer/invoicesReport/BuyerInvoicesCardList";
import { buyerInvoicesReportEndpoints as reportEndpoints } from "../../../requests/api-endpoints";
import { useReportDataSummaryCollapseByIndex}  from "../../../hooks/report/useReportDataSummaryCollapseByIndex";
import { useBoundQueryDownloadUrl } from "../../../hooks/report/useBoundQueryDownloadUrl";
import DownloadCSVButton from "../../../components/report/export/DownloadCSVButton";
import {
  DownloadButtonsContainer,
  ReportDataSummary,
  ReportSummaryWithLoader
} from "../../../components/report/redesigned";
import {
  SecurSpaceReportTitle
} from "../../../components/common";

// a default query MUST be defined outside of any components to avoid re-initialization on each render
const DEFAULT_QUERY: BuyerInvoicesSearchParams = {
  //startDate: RELATIVE DATE: define it within the component using `useGetRelativeDate`
  //endDate: RELATIVE DATE: define it within the component using `useGetRelativeDate`
  orderNumber: '',
  transactionNumber: '',
  locationId: '',
  transactionType: '',
  buyerTransactionStatus: '',
  sortBy: "TRANSACTION_NUMBER",
  sortDir: 'desc',
  page: 0,
  size: 25,
};

const QUERY_PARAM_RENAMES: Record<string, string> = {
  bookingNumber: 'orderNumber',
  invoiceNumber: 'transactionNumber',
};

const BuyerInvoicesReport = () => {
  const { snackbarShowMessage } = useSnackbar();
  // state hooks
const defaultStartDate = useGetRelativeDate(-1, 'month', DateFormats.DAY);
  const defaultEndDate = useGetRelativeDate(0, 'day', DateFormats.DAY);
  const defaultQuery = useMemo(() => ({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    ...DEFAULT_QUERY
  }), [defaultStartDate, defaultEndDate]);
  const [reportQuery, setReportQuery] = useBindSearchQuery(defaultQuery, {
    paramRenames: QUERY_PARAM_RENAMES
  });
  const requestQuery = useMemo(() => withDateRangeBypass(reportQuery, BUYER_INVOICES_DATE_BYPASS_KEYS), [reportQuery]);

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error
  } =  useGetBoundReportPaginated<InvoiceTransactionVO[]>({
    requestUrl: reportEndpoints.search,
    requestParams: requestQuery,
  });

  const {
    reportSummary,
    isReportSummaryLoading,
    reportSummaryError
  } = useGetBoundReportSummary<BuyerInvoicesSummary>({
    requestUrl: reportEndpoints.summary,
    requestParams: requestQuery,
  });

  const {
    collapseByIndex: expandByIndex, // TODO yf when redesigns slow down, rename this
    handleToggleCollapse: handleToggleExpand, // TODO yf same as above
    hasExpandedItems,
    setExpandedItems,
    setExpandedAll,
    handleExpandAll,
  } = useReportDataSummaryCollapseByIndex({ data: reportPayload });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });
  useSnackbarDisplayOnError({error: reportSummaryError, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: BuyerInvoicesSearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

  const downloadUrl = useBoundQueryDownloadUrl({ endpoint: reportEndpoints.report, query: requestQuery });

  //NOTE: since this report is contained within the ReportOverview tabs, it should not be wrapped in a
  //      SecurSpaceContainer or SecurSpaceReportContainer, as those are already present in the parent
  //      component
  return (
    <>
      <SecurSpaceReportTitle>Invoices</SecurSpaceReportTitle>
      <BuyerInvoicesSortFilter
        query={reportQuery}
        onChange={handleFilterChange}
      />
      <ReportSummaryWithLoader
        visible={!shouldShowPersonAndBox}
        isLoading={isReportSummaryLoading}
        mapping={buyerInvoicesSummaryMapping}
        summary={reportSummary}
      />
      <ReportDataSummary
        visible={!shouldShowPersonAndBox}
        count={pageInfo?.count || 0}
        title={"All"}
        isShowCollapseButton={true}
        hasExpandedItems={hasExpandedItems}
        setExpandedItems={setExpandedItems}
        setExpandedAll={setExpandedAll}
        handleExpandAll={handleExpandAll}
        downloadButtonsSlot={
          <DownloadButtonsContainer>
            <DownloadCSVButton title='Download Report' reportUrl={downloadUrl} />
          </DownloadButtonsContainer>
        }
      />
      <BuyerInvoicesCardListWithLoader
        visible={!error}
        isLoading={isLoading}
        items={reportPayload}
        expandByIndex={expandByIndex}
        onToggleExpand={handleToggleExpand}
      />
      <PersonAndBoxMessage
        visible={shouldShowPersonAndBox }
      />
      <BoundTablePagination<BuyerInvoicesSearchParams>
        visible={showPagination}
        pageInfo={pageInfo}
        onChange={handleFilterChange}
      />
    </>
  );
};

export default BuyerInvoicesReport;
